import React, {useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {useSelector} from "react-redux";
import emojiToolkit from 'emoji-toolkit';
import * as draftConvert from 'draft-convert'
import Editor from '@draft-js-plugins/editor';
import useDebouncedEffect from "use-debounced-effect";
import createEmojiPlugin from '@draft-js-plugins/emoji';
import createMentionPlugin from '@draft-js-plugins/mention';
import createToolbarPlugin from '@draft-js-plugins/static-toolbar';
import createLinkDetectionPlugin from 'draft-js-link-detection-plugin';
import createMarkdownShortcutsPlugin from 'draft-js-markdown-shortcuts-plugin';
import {ContentState, convertToRaw, EditorState, getDefaultKeyBinding, KeyBindingUtil, Modifier, RichUtils} from 'draft-js';
import {BlockquoteButton, BoldButton, CodeBlockButton, ItalicButton, OrderedListButton, UnderlineButton, UnorderedListButton,} from '@draft-js-plugins/buttons';

// Icon & Framework
import * as Icon from "ubs-icons";
import useUAlert from "ubs-framework/v2/UAlert";
import UFileAttachment from "ubs-framework/v2/UFileAttachment";
import {COLOR, FILE} from "ubs-framework/v2/common/constant";

import {sendData} from "../../../store/actions";
import useSocket from "../../common/hooks/useSocket";

// Components
import UEmployeeInfo from "../../../common/components/UEmployeeInfo";
import { FONT_STYLE_MAP, FontFamilySelect, FontSizeSelect, fontStyleFromHTML, fontStyleToHTML } from "./font-select";

// Draft CSS
import 'draft-js/dist/Draft.css';
import '@draft-js-plugins/mention/lib/plugin.css'
import '@draft-js-plugins/emoji/lib/plugin.css';
import '@draft-js-plugins/text-alignment/lib/plugin.css';
import '@draft-js-plugins/static-toolbar/lib/plugin.css'
import {useEscKey} from "ubs-framework/v2/hooks";
import {getName, isThisAFile, showToastError} from "ubs-framework/v2/common/function";

const UDraftEditor = React.forwardRef((props, ref) => {
    const alert = useUAlert();
    const editorRef = useRef(null);

    const _FL = useSelector(e => e.common?.settings?.maxFileSizeLimit);

    const [files, setFiles] = useState([]);
    const [searchMention, setSearchMention] = useState();
    const [disabled, setDisabled] = useState(true);
    const [emojiOpen, setEmojiOpen] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [removeFiles, setRemoveFiles] = useState([]);
    const [mentionOpen, setMentionOpen] = useState(false);
    const [editorState, setEditorState] = useState(EditorState.createEmpty());

    const {plugins, Toolbar, EmojiSuggestions, EmojiSelect, MentionSuggestions} = useMemo(() => {
        const mentionPlugins = createMentionPlugin({
            entityMutability: 'IMMUTABLE',
            mentionPrefix: '@',
            supportWhitespace: true
        });
        const linkDetectionPlugin = createLinkDetectionPlugin();
        const markdownPlugin = createMarkdownShortcutsPlugin()
        const emojiPlugin = createEmojiPlugin({selectButtonContent: <Icon.Emoji size={20}/>})
        const toolbarPlugin = createToolbarPlugin();
        const {EmojiSuggestions, EmojiSelect} = emojiPlugin;
        return {
            plugins: [emojiPlugin, toolbarPlugin, mentionPlugins, markdownPlugin, linkDetectionPlugin],
            Toolbar: toolbarPlugin.Toolbar,
            MentionSuggestions: mentionPlugins.MentionSuggestions,
            EmojiSuggestions,
            EmojiSelect,
        }
    }, [])

    const handle = {
        keyBind: (e, s) => {
            if (e.key === 'Escape') {
                if (props?.cancelButton) props.onCancel()
            } else if (e.keyCode === 13 && props.isButtons && !disabled) {
                return 'send-button'
            }

            return getDefaultKeyBinding(e);
        },
        keyCommand: (command, editorState) => {
            const newState = RichUtils.handleKeyCommand(editorState, command)
            if (command === 'send-button') {
                props.onSubmit(getData())
                return 'handled';
            }
            if (newState) {
                setEditorState(newState)
                return 'handled';
            }
            return 'not-handled';
        },
        return: (e, state) => {
            if (KeyBindingUtil.isSoftNewlineEvent(e)) {
                //     const newState = RichUtils.handleKeyCommand(editorState, 'split-block')
                //     if (newState) setEditorState(newState);
                //     return 'handled';
            }
            // return 'not-handled';
        },
        beforeInput: (text) => {
            if (props?.textLimit && editorState.getCurrentContent().getPlainText().length >= props?.textLimit) {
                showToastError(`You can not enter more than ${props?.textLimit} character`)
                blur()
                // alert.show({description: `You can not enter more than ${props?.textLimit} character`});
                return 'handled';
            }
        },
        pastedText: (text) => {
            if (props?.textLimit && (editorState.getCurrentContent().getPlainText().length + text.length) >= props?.textLimit) {
                showToastError(`You can not enter more than ${props?.textLimit} character`)
                blur()
                // alert.show({description: `You can not enter more than ${props?.textLimit} character`});
                return 'handled';
            }
        },
    }

    const doEmptyEditor = () => {
        blur()
        setEditorState(EditorState.createWithContent(ContentState.createFromText("")))
        setTimeout(() => focus())
    }
    const focus = () => editorRef?.current?.focus();

    const blur = () => editorRef?.current?.blur();

    const onSearchChange = ({value}) => {
        if (value.split(' ').length < 3) setSearchMention(value || '')
    }

    useDebouncedEffect(() => {
        refresh();
        sendData('hrms|dropList|get', {listType: 4, search: searchMention || ''})
        return () => off();
    }, 100, [searchMention]);

    const convert = {
        draftToHtml: (draft) => {
            return draftConvert.convertToHTML({
                entityToHTML: (entity, text) => {
                    if (entity.type === 'LINK') return <a target='_blank' className="draft-link"
                                                          href={entity.data.url}>{text}</a>;
                    if (entity?.type === "mention") return `<span class="draft-mention" mention-id=${entity.data.mention.id}>${text}</span>`
                    if (entity?.type === "emoji" && text.slice(0, 4) !== '&lt;') return `<span class="draft-emoji"> ${emojiToolkit.toImage(text)} </span>`
                    return text;
                },
                blockToHTML: (block) => {
                    if (block.text.trim() === '') {
                        return <br/>;
                    }
                },
                styleToHTML: props?.fontSelect ? fontStyleToHTML : undefined,
            })(draft.getCurrentContent())
        },
        htmlToDraft: html => {
            let updatedHTML = html
            html?.match(/\<img class="joypixels".+?\/>/g)?.map(e => {
                updatedHTML = updatedHTML?.replace(e, e.match(/alt="(.*)" t/u)[1])
            })
            const contentState = draftConvert.convertFromHTML({
                htmlToEntity: (nodeName, node, createEntity) => {
                    if (nodeName === 'a') {
                        return createEntity(
                            'LINK',
                            'MUTABLE',
                            {url: node.href.trim()}
                        )
                    }
                    if (nodeName === 'span' && node?.classList?.value === 'draft-mention') {
                        return createEntity(
                            'mention',
                            'IMMUTABLE',
                            {
                                mention: {
                                    id: node.getAttribute('mention-id'),
                                    avatar: '',
                                    name: node.innerHTML.trim(),
                                }
                            }
                        )
                    }
                    if (nodeName === 'span' && node?.classList?.value === 'draft-emoji') {
                        return createEntity(
                            'emoji',
                            'IMMUTABLE',
                            {emojiUnicode: node.innerHTML.trim()}
                        )
                    }
                },
                htmlToBlock: (nodeName, node) => {
                    if (nodeName === 'blockquote') {
                        return {
                            type: 'blockquote',
                            data: {}
                        };
                    }
                },
                htmlToStyle: props?.fontSelect ? fontStyleFromHTML : undefined
            })(updatedHTML);
            return EditorState.createWithContent(contentState);
        }
    }

    const {off, refresh} = useSocket(res => {
        if (!res.error) {
            switch (res.event) {
                case 'hrms|dropList|get':
                    const data = res?.data?.rows?.map(row => ({
                        id: row._id,
                        name: getName(row),
                        avatar: row.profileImage
                    })).slice(0, 10) || []

                    if ('all'.includes(searchMention)) data.unshift({
                        id: 'all',
                        name: 'all',
                        avatar: `/images/multi-user-icon.svg`
                    })
                    setSuggestions(data)
                    break;
                default:
                    return true;
            }
        }
    })

    useEffect(() => {
        setDisabled(files?.length === 0 && editorState?.getCurrentContent()?.getPlainText()?.trim()?.length === 0)
        props.onChange(getData())

        if (document.querySelectorAll('.public-DraftEditorPlaceholder-root')?.[0]) {
            if (document.querySelectorAll('.public-DraftStyleDefault-ul')?.[0] || document.querySelectorAll('.public-DraftStyleDefault-ol')?.[0]) {
                document.querySelectorAll('.public-DraftEditorPlaceholder-root')[0].style.display = 'none'
            } else document.querySelectorAll('.public-DraftEditorPlaceholder-root')[0].style.display = 'block'
        }
    }, [editorState, removeFiles])

    useEffect(() => {
        if (props?.emailPlaceholder?.key) {
            setEditorState(insertText(` {{${props?.emailPlaceholder?.key}}}`, editorState));
            setTimeout(() => focus());
            props.setEmailPlaceholder('')
        }
    },[props?.emailPlaceholder])

    useImperativeHandle(ref, () => ({
        doEmpty: () => {
            doEmptyEditor()
            setFiles([])
        },
        setData: (e, files) => {
            setFiles(files || [])
            setTimeout(() => setEditorState(convert.htmlToDraft(e)))
        },
        getData: () => {
            return getData()
        },
        focus: () => {
            focus()
        },
    }));

    const insertText = (text, editorState) => {
        const currentContent = editorState.getCurrentContent();
        const currentSelection = editorState.getSelection();

        const newContent = Modifier.replaceText(
            currentContent,
            currentSelection,
            text
        );

        const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
        return  EditorState.forceSelection(newEditorState, newContent.getSelectionAfter());
    }


    const getData = () => {
        const mentions = Object.values(convertToRaw(editorState.getCurrentContent()).entityMap).filter(e => e.type === 'mention').map(e => e.data.mention.id).filter(e => e)
        return {
            html: convert.draftToHtml(editorState),
            rawText: editorState?.getCurrentContent()?.getPlainText()?.trim(),
            mentions,
            removeFiles,
            files
        }
    }

    useEscKey(() => {
        if (document.getElementsByClassName('ejr02pv')[0]) {
            document.getElementsByClassName('DraftEditor-root')?.[0]?.click()
            focus()
        }
    })
    useEffect(() => {
        refresh()
        return () => off()
    }, [])

    const handleFileChange = async (e) => {
        try {
            await prepareFiles(e.target.files)
        } catch (err) {
            console.log('Error in file upload', err.message)
        }
    }
    const prepareFiles = async (data) => {
        if ((data.length + files.length) <= props.maxFiles) {
            let sizeError = false;
            const fileArray = Object.assign([], data);
            for (let i = 0; i < fileArray.length; i++) {
                if (!sizeError) sizeError = (fileArray[i].size / 1024 / 1024) > _FL;
            }
            if (!sizeError) {
                const number = data.length;
                for (let i = 0; i < number; i++) {
                    const name = fileArray[i]?.name?.split('.');
                    const ext = name[name.length - 1];
                    if (FILE.IMAGES.exec(fileArray[i]?.name) || (FILE.VIDEO.exec(fileArray[i]?.name) && !props.inlineAttachment)) {
                        if (!FILE.EXCLUDES.includes(ext)) {
                            await isThisAFile(fileArray[i]).then(validFile => {
                                setFiles(_ => [..._, {obj: validFile, url: URL.createObjectURL(validFile)}])
                            }).catch();
                        }
                    } else {
                        alert.show({title: 'Something went wrong.', description: `Unsupported File.`})
                    }
                }

            } else {
                alert.show({title: 'Something went wrong.', description: `Files must smaller than ${_FL}MB!`})
            }
        } else {
            alert.show({title: 'Something went wrong.', description: `You can upload only ${props.maxFiles} files at a time.`})
        }
    };

    useEffect(() => {
        setDisabled(files.length === 0 && editorState?.getCurrentContent()?.getPlainText()?.trim()?.length === 0)
        if (props.setFiles) props.setFiles(files)
    }, [files])

    useEffect(() => {
        if (props.autoFocus) focus()
    }, [props.autoFocus])

    return (<>
        <div className={`u_flex u_draft-editor ${props?.fontSelect ? 'u_draft-editor--font' : ''} ${props.loader && 'u_btn-disabled'}`}>
            <div className={`u_editor-container ${props.className} ${props?.isDisabled ? 'u_disabled' : ''}`} style={{ minHeight: props.height }}
                onClick={() => focus()}>
                <Editor
                    placeholder={props.placeholder}
                    editorKey={Math.random()}
                    editorState={editorState}
                    customStyleMap={props?.fontSelect ? FONT_STYLE_MAP : undefined}
                    onChange={(editorState) => setEditorState(editorState)}
                    handleKeyCommand={handle.keyCommand}
                    plugins={plugins}
                    ref={editorRef}
                    preserveSelectionOnBlur={true}
                    keyBindingFn={!emojiOpen && !mentionOpen && handle.keyBind}
                    handleBeforeInput={handle.beforeInput}
                    handlePastedText={handle.pastedText}
                    handleReturn={handle.return}
                />
                <div className='u_draft-emoji-wrapper'>
                    <EmojiSuggestions onOpen={() => setEmojiOpen(true)} onClose={() => setEmojiOpen(false)}/>
                </div>
                <MentionSuggestions
                    open={mentionOpen}
                    onOpenChange={(e) => setMentionOpen(e)}
                    suggestions={suggestions}
                    onSearchChange={onSearchChange}
                    onAddMention={() => {
                        // get the mention object selected
                    }}
                    entryComponent={MentionItem}
                />
            </div>
            {
                props?.isButtons &&
                <div className="u_draft-actions">
                    {
                        props?.cancelButton &&
                        <span className="u_draft-actions-icons" onClick={() => props?.onCancel()}>
                            <Icon.Close size={16} fill={COLOR.u_icon_bg}/>
                        </span>
                    }

                    <span className='u_draft-actions-icons u_emoji inline-emoji-btn'>
                        <EmojiSelect closeOnEmojiSelect/>
                    </span>

                    {
                        files?.length < props.maxFiles && props?.inlineAttachment &&
                        <div className={`u_draft-actions-icons u_relative`}>
                            <input type='file' className="u_attach-more-input u_cp" title=""
                                   onClick={(e) => e.target.value = ""}
                                   id={`u_upload`} accept="image/*"
                                   autoComplete="off" multiple={false} onChange={handleFileChange}/>
                            <Icon.Attachment size={16}/>
                        </div>
                    }
                    {
                        (!!editorState?.getCurrentContent()?.getPlainText()?.trim() || !!files?.length) &&
                        <span className="u_draft-actions-icons" onClick={() => props?.onSubmit(getData())}>
                            <Icon.SendMessage size={16} fill={COLOR.u_blue}/>
                        </span>
                    }
                </div>
            }

            {
                !props?.hideToolbar && <div className={`u_emoji u_w-full ${props?.isDisabled ? 'u_disabled' : ''}`}><Toolbar>
                    {externalProps => {
                        let overrideProps = { getEditorState: () => editorState, setEditorState }
                        return <div className={`u_flex ${props?.fontSelect ? 'u_align-center u_flex-wrap u_w-full u_editor-toolbar' : ''}`}>
                            {
                                props?.fontSelect &&
                                <>
                                    <FontFamilySelect editorState={editorState} onChange={setEditorState} disabled={props?.isDisabled} />
                                    <FontSizeSelect editorState={editorState} onChange={setEditorState} disabled={props?.isDisabled} />
                                    <span className="u_editor-toolbar-break"/>
                                </>
                            }
                            <BoldButton {...externalProps} {...overrideProps}/>
                            <ItalicButton {...externalProps} {...overrideProps}/>
                            <UnderlineButton {...externalProps} {...overrideProps}/>
                            <CodeBlockButton {...externalProps} {...overrideProps}/>
                            <UnorderedListButton {...externalProps} {...overrideProps}/>
                            <OrderedListButton {...externalProps} {...overrideProps}/>
                            <BlockquoteButton {...externalProps} {...overrideProps}/>
                            {props.emojiSelect && <div className='u_p-5 u_flex u_item-center'><EmojiSelect closeOnEmojiSelect/></div>}
                            {/*{files?.length < props.maxFiles && <div className={`u_attach-editor`}>*/}
                            {/*    <input type='file' className="u_attach-more-input u_cp" title=""*/}
                            {/*           onClick={(e) => e.target.value = ""}*/}
                            {/*           id={`u_upload`} accept="video/*,image/*"*/}
                            {/*           autoComplete="off" multiple={true} onChange={handleFileChange}/>*/}
                            {/*    <Icon.Attachment size={18}/>*/}
                            {/*</div>}*/}
                            {props?.Templates && props?.Templates}
                        </div>
                    }}
                </Toolbar></div>
            }

        </div>

        {
            files?.length > 0 &&
            <div className="u_draft-files">
                {props.loader ?
                    <Icon.Preloader size={60}/> :
                    <UFileAttachment files={files} setFiles={setFiles} size={60} showInput={false} maxFiles={5}
                                    options={['delete', 'view']} setRemoveFiles={setRemoveFiles}/>}
            </div>
        }

    </>);
});

export default React.memo(UDraftEditor);

UDraftEditor.defaultProps = {
    className: '',
    height: 40,
    textLimit: 0,
    maxFiles: 1,
    hideToolbar: false,
    // Option font family + size support
    fontSelect: false,
    cancelButton: false,
    loader: false,
    autoFocus: false,
    emojiSelect: false,
    placeholder: '',
    isDisabled: false,
    onChange: () => {
    },
    setFiles: () => {
    },
    isButtons: false, // for inline send button and attachment icon needed then pass inlineAttachment
    inlineAttachment: false,
    onSubmit: () => {
    }, // inline send button submit click
    onCancel: () => {
    }, // inline cancel button to close edit comment
};

const MentionItem = (props) => {

    const {mention, theme, searchValue, isFocused, ...parentProps} = props;

    return (
        <div {...parentProps} className={`u_mention-user ${isFocused && 'active'}`}>
            <UEmployeeInfo nameClass={'u_mention-user__name'} size={26} addS3Url={mention.id !== 'all'}
                           employee={{...mention, profileImage: mention.avatar}}/>
        </div>
    );
}
